import React, { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { ActionSection, SideCancelBtn, SubmitBtn } from "../Styles/InputStyles";

const ManagementDetailsModal = ({ onClose, managementSettingsData, editManagementDetailMutate, editManagementDetailLoading, isLoading,
  isDataLoaded }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight); // updates when Safari UI changes
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const [formData, setFormData] = useState({
    name: managementSettingsData?.name || "",
    email: managementSettingsData?.email || "",
    mobile: managementSettingsData?.mobile || "",
    businessName: managementSettingsData.businessName || "",
    gstNo: managementSettingsData?.gstNo || "",
  });

  const handleSubmit = (values) => {
    if (!values.name) {
      toast.error("Name is required");
      return;
    }
    if (!values.email) {
      toast.error("Email is required");
      return;
    }
    if (!values.mobile) {
      toast.error("Mobile is required");
      return;
    }

    if (!/^[0-9]{10}$/.test(values.mobile)) {
      toast.error("Mobile number must be 10 digits");
      return;
    }

    editManagementDetailMutate(values);
  };

  return (
    <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Header>
        <Title className="text-white">
          {isEditing ? "Edit Company Details" : "Company Details"}
        </Title>
        <button onClick={onClose}>
          <MdClose color="white" size={24} />
        </button>
      </Header>
      {isLoading || !isDataLoaded ? (
        <div className="flex items-center justify-center h-full">
          <CircularProgress />

        </div>
      ) : (
        <>
          {isEditing ? (
            <Formik
              initialValues={formData}
              onSubmit={handleSubmit}
            >
              {({ values, handleChange, errors, touched }) => (
                <Form className="px-6 h-full flex flex-col justify-between py-4 relative">
                  <Wrapper>
                    <InputGroup>
                      <Label>Name</Label>
                      <Input
                        id="name"
                        type="text"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                      />
                      {touched.name && errors.name && <Error>{errors.name}</Error>}
                    </InputGroup>

                    <InputGroup>
                      <Label>Email</Label>
                      <Input
                        id="email"
                        type="text"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                      />
                      {touched.email && errors.email && (
                        <Error>{errors.email}</Error>
                      )}
                    </InputGroup>

                    <InputGroup>
                      <Label>Mobile</Label>
                      <Input
                        id="mobile"
                        type="text"
                        name="mobile"
                        inputMode="numeric"
                        value={values.mobile}
                        onChange={handleChange}
                        maxLength={10}
                      />
                      {touched.mobile && errors.mobile && (
                        <Error>{errors.mobile}</Error>
                      )}
                    </InputGroup>

                    <InputGroup>
                      <Label>Business Name</Label>
                      <Input
                        id="businessName"
                        type="text"
                        name="businessName"
                        value={values.businessName}
                        onChange={handleChange}
                      />
                      {touched.businessName && errors.businessName && (
                        <Error>{errors.businessName}</Error>
                      )}
                    </InputGroup>

                    <InputGroup>
                      <Label>GST No.</Label>
                      <Input
                        id="gstNo"
                        type="text"
                        name="gstNo"
                        value={values.gstNo}
                        onChange={handleChange}
                      />
                      {touched.gstNo && errors.gstNo && (
                        <Error>{errors.gstNo}</Error>
                      )}
                    </InputGroup>
                  </Wrapper>
                  <ActionSection $isEditing={isEditing}>
                    <SideCancelBtn
                      type="button"
                      $isEditing={isEditing}
                      onClick={() => setIsEditing(false)}
                    >
                      Back
                    </SideCancelBtn>
                    <SubmitBtn $isEditing={isEditing} type="submit" disable={editManagementDetailLoading}>
                      {editManagementDetailLoading ? "Updating..." : "Update"}

                    </SubmitBtn>
                  </ActionSection>

                  <BtnWrapper $isEditing={isEditing}>
                    <CancelButton
                      onClick={onClose}
                    >
                      Cancel
                    </CancelButton>
                    <PrimaryButton $isEditing={isEditing} type="submit" disable={editManagementDetailLoading}>
                      {editManagementDetailLoading ? "Updating" : "Update"}
                    </PrimaryButton>
                  </BtnWrapper>
                </Form>
              )}
            </Formik>
          ) : (
            <div className="px-6 h-full flex flex-col justify-between py-4 relative">
              <Wrapper>
                <SectionTitle>Management & Company Details</SectionTitle>
                <Section>
                  <Row>
                    <Label>Name</Label>
                    <Value>{managementSettingsData.name || "--"}</Value>
                  </Row>
                  <Row>
                    <Label>Email</Label>
                    <Value>{managementSettingsData.email || "--"}</Value>
                  </Row>
                  <Row>
                    <Label>Mobile</Label>
                    <Value>{managementSettingsData.mobile || "--"}</Value>
                  </Row>
                  <Row>
                    <Label>Business Name</Label>
                    <Value>{managementSettingsData.businessName || "--"}</Value>
                  </Row>
                  <Row>
                    <Label>GST No.</Label>
                    <Value>{managementSettingsData.gstNo || "--"}</Value>
                  </Row>
                </Section>
              </Wrapper>
              <ActionSection $isEditing={isEditing}>
                <SideCancelBtn
                  type="button"
                  onClick={onClose}
                >
                  Cancel
                </SideCancelBtn>
                <SubmitBtn $isEditing={isEditing}
                  onClick={() => setIsEditing(true)}>
                  Edit
                </SubmitBtn>
              </ActionSection>

            </div>
          )}
        </>)}
    </Container>
  );
};

const Wrapper = tw.div`grid gap-2 my-0`;
const Container = tw.div`bg-white w-full h-full flex flex-col`;
const Title = tw.h1`text-lg md:text-xl font-medium text-center text-gray-800`;
const Section = tw.div``;
const SectionTitle = tw.h2`text-lg font-semibold text-gray-700 mb-2`;
const Row = tw.div`flex justify-between py-2 md:p-2`;
const Label = tw.label`text-sm text-gray-500 font-normal`;
const Value = tw.span`text-gray-800 font-medium`;
const InputGroup = tw.div`flex flex-col space-y-1.5 mb-1`;
const Input = tw.input`border border-gray-300 rounded h-10 px-2 py-1 focus:ring-1 focus:ring-primary-color focus:border-primary-color focus:outline-none`;
const Error = tw.p`text-rose-600 text-sm`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;

const BtnWrapper = tw.div`${({ $isEditing }) =>
  $isEditing
    ? "flex gap-3"
    : "flex flex-col sm:flex-row gap-3 w-full sm:justify-between mt-4"}`;

const PrimaryButton = tw.button`${({ $isEditing }) =>
  $isEditing
    ? "bg-primary-color w-1/2 text-white px-4 py-2 rounded-lg hover:bg-[#65adf0]"
    : "bg-primary-color w-full sm:w-1/2 text-white px-4 py-2 rounded-lg hover:bg-[#65adf0]"}`;

const CancelButton = tw.button`"underline-offset-4 bg-white w-1/2 text-gray-400 font-medium px-4 py-1 rounded-lg"}`;

export default ManagementDetailsModal;
